// Фоновые задачи: один опрос на всё приложение.
//
// Раньше опрашивала сама панель, и знала о задачах только она — то есть
// только когда её открыли. Теперь кнопка, которая панель открывает, сама
// решает, показываться ли ей (агентский тред или живая задача), и для этого
// счётчик нужен ДО открытия. Два опроса на одни и те же данные — это два
// расхождения, поэтому опрос переехал сюда, а панель стала отображением.
//
// Push here is still impossible for the reason `web/src/tasks.rs` explains at
// length: the runner that owns a task is usually the *other* container, and
// there is no broker between them. So this polls, and the whole design is
// about polling as little as the answer allows.

import { useCallback, useEffect, useRef, useState } from 'react'
import { api, type Approval, type Schedule, type Task } from './api'

/** Пока панель открыта и что-то живое: видно, как идут шаги. */
const POLL_OPEN_MS = 4000

/**
 * Панель закрыта, но что-то живое: следим, чтобы заметить окончание и убрать
 * кнопку. Реже, потому что единственное, что меняется на экране, — цифра на
 * значке.
 */
const POLL_IDLE_MS = 20000

/** Задача, которая ещё может измениться сама по себе. */
function isLive(t: Task): boolean {
  return (
    t.state === 'running' ||
    t.state === 'queued' ||
    t.state === 'proposed' ||
    t.state === 'waiting_approval'
  )
}

export interface TasksState {
  tasks: Task[]
  schedules: Schedule[]
  approval: Approval | null
  /** Сколько задач ещё в игре — столько же стоит на значке. */
  liveCount: number
  error: string | null
  busy: boolean
  reload: () => Promise<void>
  /** Выполняет действие и перечитывает состояние. */
  act: (fn: () => Promise<unknown>) => Promise<void>
}

export function useTasks(open: boolean): TasksState {
  const [tasks, setTasks] = useState<Task[]>([])
  const [schedules, setSchedules] = useState<Schedule[]>([])
  const [approval, setApproval] = useState<Approval | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const reload = useCallback(async () => {
    try {
      const r = await api.tasks()
      setTasks(r.tasks)
      setSchedules(r.schedules)
      setApproval(r.approval)
      setError(null)
    } catch (e) {
      // Ошибку показываем только в открытой панели: всплывающее сообщение о
      // фоновом опросе — это шум поверх чата, в котором человек занят другим.
      setError(e instanceof Error ? e.message : 'не вышло получить задачи')
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  const live = tasks.filter(isLive)
  const liveCount = live.length
  const watching = liveCount > 0 || approval !== null

  // Ничего живого и панель закрыта — не опрашиваем вовсе. Список из одних
  // законченных задач не меняется сам, и дёргать сервер ради этого незачем.
  const period = open ? POLL_OPEN_MS : watching ? POLL_IDLE_MS : 0
  useEffect(() => {
    if (period === 0) return
    const id = setInterval(() => void reload(), period)
    return () => clearInterval(id)
  }, [period, reload])

  // Открытие панели — это всегда свежий взгляд: между последним опросом и
  // нажатием могло пройти двадцать секунд.
  const wasOpen = useRef(open)
  useEffect(() => {
    if (open && !wasOpen.current) void reload()
    wasOpen.current = open
  }, [open, reload])

  const act = useCallback(
    async (fn: () => Promise<unknown>) => {
      setBusy(true)
      try {
        await fn()
        await reload()
      } catch (e) {
        setError(e instanceof Error ? e.message : 'не вышло')
      } finally {
        setBusy(false)
      }
    },
    [reload],
  )

  return { tasks, schedules, approval, liveCount, error, busy, reload, act }
}
